import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

/**
 * Lightweight searchable combobox (no external dependency).
 *
 * Reuses the form's `.dq-register-txt` input styling so it sits flush with the
 * native fields; the dropdown panel is styled here via styled-components.
 *
 * Props:
 *   options        Array<{ value: string, label: string }>
 *   value          currently selected `value`
 *   onChange       (value) => void
 *   placeholder    shown when empty
 *   noResultsText  shown when the filter matches nothing
 *   id, name, disabled
 */
const Wrap = styled.div`
    position: relative;
    width: 100%;
`;

const Caret = styled.span`
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 1vh;
    color: #066f7f;
    pointer-events: none;
    font-size: 1.2rem;
`;

const Panel = styled.ul`
    position: absolute;
    z-index: 60;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 1px solid gray;
    border-radius: 10px;
    max-height: 240px;
    overflow-y: auto;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
`;

const Item = styled.li`
    padding: 0.8vh 1.2vh;
    cursor: pointer;
    text-align: left;
    font-size: 1rem;
    color: #333;
    background: ${(p) => (p.$active ? "#e6f4f6" : "transparent")};
    &:hover {
        background: #e6f4f6;
    }
`;

const Empty = styled.li`
    padding: 0.8vh 1.2vh;
    color: gray;
    font-style: italic;
    text-align: left;
    font-size: 0.95rem;
`;

const SearchableSelect = ({ options = [], value, onChange, placeholder = "", noResultsText = "No results", id, name, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);

    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) => o.label.toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q)
        );
    }, [options, query]);

    // Close (without changing selection) when clicking outside.
    useEffect(() => {
        const onDocMouseDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, []);

    // Reset highlight when the visible list changes.
    useEffect(() => { setActive(0); }, [query]);

    // Keep the highlighted row in view.
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.children[active];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }, [active, open]);

    const openMenu = () => {
        if (disabled || open) return; // don't wipe an in-progress query if already open
        setQuery("");
        setActive(0);
        setOpen(true);
    };

    const choose = (opt) => {
        onChange(opt.value);
        setOpen(false);
        setQuery("");
        if (inputRef.current) inputRef.current.blur();
    };

    const onKeyDown = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { openMenu(); return; }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[active]) choose(filtered[active]);
        } else if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
            if (inputRef.current) inputRef.current.blur();
        }
    };

    const inputValue = open ? query : (selected ? selected.label : "");
    const shownPlaceholder = open && selected ? selected.label : placeholder;
    const listId = `${id || name || "ss"}-listbox`;

    return (
        <Wrap ref={wrapRef}>
            <input
                ref={inputRef}
                className="dq-register-txt"
                type="text"
                id={id}
                name={name}
                autoComplete="off"
                disabled={disabled}
                placeholder={shownPlaceholder}
                value={inputValue}
                role="combobox"
                aria-expanded={open}
                aria-controls={listId}
                aria-autocomplete="list"
                onFocus={openMenu}
                onClick={openMenu}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onKeyDown={onKeyDown}
            />
            <Caret>▼</Caret>
            {open && (
                <Panel ref={listRef} id={listId} role="listbox">
                    {filtered.length === 0 && <Empty>{noResultsText}</Empty>}
                    {filtered.map((opt, i) => (
                        <Item
                            key={opt.value}
                            role="option"
                            aria-selected={opt.value === value}
                            $active={i === active}
                            // onMouseDown (not onClick) so selection runs before the input blurs.
                            onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                            onMouseEnter={() => setActive(i)}
                        >
                            {opt.label}
                        </Item>
                    ))}
                </Panel>
            )}
        </Wrap>
    );
};

export default SearchableSelect;
